import React, { useMemo, useState } from "react";
import { Sparkles, CheckCircle2, Layers, Search, ArrowUpRight } from "lucide-react";
import { experienceDetails } from "../mock";

function Pill({ children, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`about-immersive__chip ${active ? "luxe-nav__link--active" : ""}`}
      style={{ cursor: "pointer" }}
    >
      {children}
    </button>
  );
}

export default function Experience() {
  const [activeId, setActiveId] = useState(experienceDetails[0]?.id);
  const [query, setQuery] = useState("");

  const active = useMemo(
    () => experienceDetails.find((x) => x.id === activeId) ?? experienceDetails[0],
    [activeId]
  );

  // Skills sidebar removed as requested (keep role narrative only)

  const spotlight = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.trim().toLowerCase();
    const matches = (active.howIUsedThem ?? []).filter(
      (x) => x.skill.toLowerCase().includes(q) || x.applied.toLowerCase().includes(q)
    );
    return matches.length ? matches : null;
  }, [active, query]);

  return (
    <div className="page" style={{ paddingTop: 96 }}>
      <div
        style={{
          padding: "0 clamp(2.2rem, 6vw, 8rem)",
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        <div className="contact-label-luxe" style={{ marginBottom: 12 }}>
          <Sparkles size={14} />
          <span>EXPERIENCE</span>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 18,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <h1
            className="contact-title-luxe"
            style={{ margin: 0, fontSize: "clamp(2.2rem, 4.8vw, 3.6rem)" }}
          >
            Work, impact,
            <br />
            <span className="contact-title-gradient">and what I learned</span>
          </h1>

          <div style={{ display: "grid", gap: 10, justifyItems: "end" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 18,
                border: "1px solid rgba(255,182,217,0.14)",
                background: "rgba(255,255,255,0.03)",
                backdropFilter: "blur(16px)",
                color: "rgba(255,255,255,0.78)",
              }}
            >
              <Search size={16} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search skills (e.g., DCF, scenario, dashboard…)"
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: "rgba(255,255,255,0.90)",
                  width: 320,
                }}
              />
            </div>
            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
              Type to spotlight where a skill shows up.
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: 26,
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: 18,
            alignItems: "start",
          }}
        >
          <aside style={{ gridColumn: "span 4" }}>
            <div className="pillars-scrollpin__content" style={{ padding: 18 }}>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: ".28em",
                  textTransform: "uppercase",
                  color: "rgba(255,182,217,0.8)",
                }}
              >
                Roles
              </div>

              <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                {experienceDetails.map((e) => {
                  const isActive = e.id === activeId;
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => setActiveId(e.id)}
                      className="pillars-scrollpin__content"
                      style={{
                        textAlign: "left",
                        cursor: "pointer",
                        padding: 16,
                        borderColor: isActive
                          ? "rgba(255,182,217,0.35)"
                          : "rgba(255,182,217,0.14)",
                        background: isActive
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(255,255,255,0.03)",
                        transition: "background-color .25s ease, border-color .25s ease",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: "Playfair Display, Georgia, serif",
                          fontSize: 18,
                          color: "rgba(255,255,255,0.92)",
                          lineHeight: 1.2,
                        }}
                      >
                        {e.role}
                      </div>
                      <div style={{ marginTop: 8, color: "rgba(255,255,255,0.72)", lineHeight: 1.6 }}>
                        {e.company}
                      </div>
                      <div style={{ marginTop: 6, color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
                        {e.period}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Skills block removed */}
          </aside>

          <main style={{ gridColumn: "span 8" }}>
            <section className="about-immersive__quote" style={{ textAlign: "left" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "baseline",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: ".28em",
                      textTransform: "uppercase",
                      color: "rgba(255,182,217,0.82)",
                    }}
                  >
                    {active.company} • {active.location}
                  </div>
                  <h2
                    style={{
                      margin: "10px 0 0",
                      fontFamily: "Playfair Display, Georgia, serif",
                      fontSize: 28,
                      lineHeight: 1.18,
                    }}
                  >
                    {active.role}
                  </h2>
                  <div style={{ marginTop: 8, color: "rgba(255,255,255,0.65)", fontSize: 12 }}>
                    {active.period}
                  </div>
                </div>

                <a
                  href="#contact"
                  className="luxe-nav__link"
                  style={{ alignSelf: "flex-start" }}
                >
                  Contact <ArrowUpRight size={14} style={{ marginLeft: 6, display: "inline" }} />
                </a>
              </div>

              <p style={{ marginTop: 16, color: "rgba(255,255,255,0.82)", lineHeight: 1.9 }}>
                {active.summary}
              </p>

              <div style={{ marginTop: 18, display: "grid", gap: 14 }}>
                <div className="pillars-scrollpin__content" style={{ padding: 18 }}>
                  <div
                    style={{
                      display: "inline-flex",
                      gap: 10,
                      alignItems: "center",
                      color: "rgba(255,255,255,0.78)",
                      fontSize: 12,
                      letterSpacing: ".26em",
                      textTransform: "uppercase",
                    }}
                  >
                    <Layers size={14} />
                    Impact
                  </div>
                  <ul style={{ marginTop: 12, color: "rgba(255,255,255,0.78)", lineHeight: 1.9 }}>
                    {(active.impact ?? []).map((b) => (
                      <li key={b} style={{ marginTop: 6 }}>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pillars-scrollpin__content" style={{ padding: 18 }}>
                  <div
                    style={{
                      display: "inline-flex",
                      gap: 10,
                      alignItems: "center",
                      color: "rgba(255,255,255,0.78)",
                      fontSize: 12,
                      letterSpacing: ".26em",
                      textTransform: "uppercase",
                    }}
                  >
                    <CheckCircle2 size={14} />
                    Skills acquired
                  </div>
                  <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {(active.skillsAcquired ?? []).map((s) => (
                      <span key={s} className="pillars-scrollpin__output">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pillars-scrollpin__content" style={{ padding: 18 }}>
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: ".28em",
                      textTransform: "uppercase",
                      color: "rgba(255,182,217,0.82)",
                    }}
                  >
                    How I implemented these skills
                  </div>

                  {(spotlight ?? active.howIUsedThem ?? []).map((x) => (
                    <div key={x.skill} style={{ marginTop: 14 }}>
                      <div
                        style={{
                          fontFamily: "Playfair Display, Georgia, serif",
                          fontSize: 18,
                          color: "rgba(255,255,255,0.92)",
                        }}
                      >
                        {x.skill}
                      </div>
                      <div style={{ marginTop: 8, color: "rgba(255,255,255,0.78)", lineHeight: 1.9 }}>
                        {x.applied}
                      </div>
                    </div>
                  ))}

                  {!!spotlight && (
                    <div style={{ marginTop: 14, color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
                      Showing matches for “{query.trim()}”. Clear search to see all.
                    </div>
                  )}
                </div>
              </div>
            </section>

            <div style={{ height: 80 }} />
          </main>
        </div>
      </div>

      <style>{`
        @media (max-width: 980px){
          .page > div[style*="grid-template-columns"]{
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
